/**
 * 首页 —— 今日作业工作台（视觉对齐 docs/操作人员移动端 设计稿）。
 * 浅色顶栏「蓝星现场」+ 今日概览三卡（待处理/进行中/已完成）
 * + 快捷入口（扫码查柜 / 异常上报，占位）+ 今日待办 Top3。
 *
 * 与任务中心（/mobile/tasks）区分：首页只给「一眼看完今天状态」的摘要视图，
 * 完整任务列表与历史交给任务中心。退出登录统一收口到「我的」。
 */
import { Box, Typography, CircularProgress, Snackbar, Alert, ButtonBase } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TaskCard from '@/components/mobile/TaskCard';
import { MobileAppBar, StatGrid, SectionHead, EmptyState, cardSx } from '@/components/mobile/ui';
import { getTodo } from '@/api/fulfillment';
import type { TodoItem } from '@/api/types';
import { T } from '@/theme/tokens';

export default function Home() {
  const navigate = useNavigate();

  const [todoList, setTodoList] = useState<TodoItem[]>([]);
  const [doneCount, setDoneCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [todo, done] = await Promise.all([getTodo('TO_DO'), getTodo('DONE')]);
        if (!alive) return;
        const todoArr = Array.isArray(todo) ? todo : [];
        const doneArr = Array.isArray(done) ? done : [];
        setTodoList(todoArr);
        setDoneCount(doneArr.length);
        setPendingCount(todoArr.filter((t) => t.taskStatus === 'PENDING').length);
      } catch {
        /* 静默 */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const inProgressCount = todoList.filter((t) => t.taskStatus === 'IN_PROGRESS').length;
  const dash = <Box component="span" sx={{ color: T.ink4 }}>-</Box>;

  const handleProcess = (task: TodoItem) => {
    const target =
      task.nodeCode === 'load' ? `/mobile/load/${task.orderId}` : `/mobile/task/${task.orderId}`;
    navigate(target, { state: { nodeCode: task.nodeCode, orderNo: task.orderNo || task.orderId } });
  };

  const quickActions = [
    { label: '扫码查柜', desc: '一键核验柜号', icon: <QrCodeScannerIcon />, key: 'scan', fg: T.brand, bg: T.brandSoft },
    { label: '异常上报', desc: '拍照 · 定位', icon: <ReportProblemOutlinedIcon />, key: 'report', fg: T.amber, bg: T.amberSoft },
  ];

  return (
    <Box>
      <MobileAppBar title="蓝星现场" />

      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        {/* 今日概览 */}
        <StatGrid
          items={[
            { label: '待处理', value: loading ? dash : pendingCount, tone: 'amber', onClick: () => navigate('/mobile/tasks') },
            { label: '进行中', value: loading ? dash : inProgressCount, tone: 'blue', onClick: () => navigate('/mobile/tasks?tab=DOING') },
            { label: '已完成', value: loading ? dash : doneCount, tone: 'green', onClick: () => navigate('/mobile/tasks?tab=DONE') },
          ]}
        />

        {/* 快捷入口 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px', mt: '14px', mb: '18px' }}>
          {quickActions.map((a) => (
            <ButtonBase
              key={a.key}
              onClick={() => setSnack({ msg: `${a.label}功能即将上线，敬请期待`, sev: 'info' })}
              sx={{
                ...cardSx,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1.5,
                px: '14px',
                py: '15px',
                textAlign: 'left',
                fontFamily: 'inherit',
                transition: 'transform .16s',
                '&:active': { transform: 'scale(.985)' },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: a.bg,
                  color: a.fg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 22 },
                }}
              >
                {a.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: T.ink }}>{a.label}</Typography>
                <Typography sx={{ fontSize: '11.5px', color: T.ink3, mt: '2px' }}>{a.desc}</Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>

        {/* 今日待办 Top3 */}
        <SectionHead
          title="今日待办"
          count={loading ? undefined : todoList.length}
          moreText="全部 ›"
          onMore={() => navigate('/mobile/tasks')}
        />

        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && todoList.length === 0 && (
          <Box sx={{ ...cardSx }}>
            <EmptyState
              icon={<AssignmentTurnedInOutlinedIcon />}
              title="今日暂无待办"
              desc="新的节点任务派发后会出现在这里"
            />
          </Box>
        )}
        {!loading &&
          todoList.slice(0, 3).map((t) => <TaskCard key={t.taskId || t.orderId} task={t} onAction={handleProcess} />)}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
